import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { appointmentsApi, teachersApi, teacherAvailabilityApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, Check, X, ChalkboardTeacher } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const Appointments = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Availability for the logged-in teacher (or admin selecting one)
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [availability, setAvailability] = useState([]);
  const [availForm, setAvailForm] = useState({ day_of_week: '1', start_time: '08:00', end_time: '17:00', slot_duration: '30' });

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (selectedTeacherId) loadAvailability(selectedTeacherId);
  }, [selectedTeacherId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [apptRes, teachersRes] = await Promise.all([
        appointmentsApi.getAll({}),
        teachersApi.getAll()
      ]);
      setAppointments(apptRes.data || []);
      setTeachers(teachersRes.data || []);
      if (!selectedTeacherId && teachersRes.data?.length) {
        setSelectedTeacherId(teachersRes.data[0].id);
      }
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async (teacherId) => {
    try {
      const { data } = await teacherAvailabilityApi.get(teacherId);
      setAvailability(data || []);
    } catch {
      // ignore
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await appointmentsApi.updateStatus(id, { status });
      toast.success(t('success'));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    }
  };

  const handleSetAvailability = async (e) => {
    e.preventDefault();
    if (!selectedTeacherId) return;
    try {
      await teacherAvailabilityApi.set({
        teacher_id: selectedTeacherId,
        day_of_week: Number(availForm.day_of_week),
        start_time: availForm.start_time,
        end_time: availForm.end_time,
        slot_duration: Number(availForm.slot_duration) || 30
      });
      toast.success(t('success'));
      loadAvailability(selectedTeacherId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    }
  };

  const dayLabels = [
    t('dayMonday'), t('dayTuesday'), t('dayWednesday'),
    t('dayThursday'), t('dayFriday'), t('daySaturday'), t('daySunday')
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center gap-3">
        <Calendar size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('appointments')}</h1>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="list" data-testid="tab-appt-list">{t('appointments')}</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">{t('myAvailability')}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('date')}</TableHead>
                    <TableHead>{t('teacher')}</TableHead>
                    <TableHead>{language === 'fr' ? 'Élève' : 'Student'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Parent' : 'Parent'}</TableHead>
                    <TableHead>{t('reason')}</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell></TableRow>
                  ) : (
                    appointments.map((a) => (
                      <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                        <TableCell className="font-mono text-sm">{a.date} {a.time}</TableCell>
                        <TableCell>{a.teacher_name}</TableCell>
                        <TableCell>{a.student_name}</TableCell>
                        <TableCell>{a.parent_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{a.reason}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || ''}`}>{t(a.status)}</span>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {a.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'confirmed')} data-testid={`confirm-appt-${a.id}`}>
                              <Check size={14} className="mr-1" />{t('confirmAppointment')}
                            </Button>
                          )}
                          {a.status === 'confirmed' && (
                            <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'completed')} data-testid={`complete-appt-${a.id}`}>
                              {t('completeAppointment')}
                            </Button>
                          )}
                          {['pending', 'confirmed'].includes(a.status) && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => updateStatus(a.id, 'cancelled')} data-testid={`cancel-appt-${a.id}`}>
                              <X size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="availability" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit flex items-center gap-2"><ChalkboardTeacher size={20} />{t('setAvailability')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('teacher')}</Label>
                <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
                  <SelectTrigger className="w-full md:w-96" data-testid="avail-teacher-select"><SelectValue placeholder={t('selectTeacher')} /></SelectTrigger>
                  <SelectContent>
                    {teachers.map(tt => (
                      <SelectItem key={tt.id} value={tt.id}>{tt.first_name} {tt.last_name} {tt.subject ? `(${tt.subject})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <form onSubmit={handleSetAvailability} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Jour' : 'Day'}</Label>
                  <Select value={availForm.day_of_week} onValueChange={(v) => setAvailForm({ ...availForm, day_of_week: v })}>
                    <SelectTrigger data-testid="avail-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {dayLabels.map((lbl, idx) => (
                        <SelectItem key={idx} value={String(idx)}>{lbl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('startTime')}</Label>
                  <Input type="time" data-testid="avail-start" value={availForm.start_time} onChange={(e) => setAvailForm({ ...availForm, start_time: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>{t('endTime')}</Label>
                  <Input type="time" data-testid="avail-end" value={availForm.end_time} onChange={(e) => setAvailForm({ ...availForm, end_time: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>{t('slotDuration')}</Label>
                  <Input type="number" data-testid="avail-duration" value={availForm.slot_duration} onChange={(e) => setAvailForm({ ...availForm, slot_duration: e.target.value })} required min={5} step={5} />
                </div>
                <Button type="submit" data-testid="save-avail-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">{t('save')}</Button>
              </form>

              <div className="border-t pt-4">
                <h3 className="font-outfit font-semibold mb-3">{t('myAvailability')}</h3>
                {availability.length === 0 ? (
                  <p className="text-sm text-[#64748B]">{t('noData')}</p>
                ) : (
                  <div className="space-y-2">
                    {availability.map((s) => (
                      <div key={s.id} className="flex items-center gap-4 p-3 rounded-lg bg-[#F8FAFC]" data-testid={`avail-row-${s.day_of_week}`}>
                        <Clock size={16} className="text-[#0052FF]" />
                        <span className="font-medium w-24">{dayLabels[s.day_of_week]}</span>
                        <span className="text-sm text-[#64748B]">{s.start_time} - {s.end_time}</span>
                        <span className="text-xs text-[#64748B]">({s.slot_duration} min)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Appointments;
